import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Fingerprint, Smartphone, Wifi, Shield, DoorOpen, CreditCard, User } from "lucide-react";

export default function SmartLockAccessControl() {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";

  const features = [
    { icon: Lock, key: "entranceControl", color: "#ffde21" },
    { icon: DoorOpen, key: "doorCompatibility", color: "#ffde21" },
    { icon: Fingerprint, key: "authMethods", color: "#ffde21" },
    { icon: Smartphone, key: "mobileApp", color: "#ffde21" },
    { icon: Wifi, key: "connectivity", color: "#ffde21" }
  ];

  const authMethods = [
    { icon: User, title: dir === "rtl" ? "بصمة الوجه" : "Face Recognition" },
    { icon: Fingerprint, title: dir === "rtl" ? "بصمة الإصبع" : "Fingerprint" },
    { icon: Lock, title: dir === "rtl" ? "رقم سري" : "Password" },
    { icon: CreditCard, title: dir === "rtl" ? "بطاقات ذكية" : "Smart Cards" }
  ];

  return (
    <div dir={dir} className="min-h-screen" style={{ background: '#fafcf0' }}>
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #2d2d2b 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="flex flex-col md:flex-row items-center gap-12 md:gap-16">
            {/* Image Side */}
            <motion.div
              initial={{ opacity: 0, x: dir === "rtl" ? 50 : -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="md:w-1/2"
            >
              <div className="relative">
                <div
                  className="absolute -inset-4 rounded-3xl opacity-20 blur-2xl"
                  style={{ background: '#ffde21' }}
                ></div>
                <img
                  src="/images/SmartLockandAccess.webp"
                  alt={t("accessControl")}
                  className="relative rounded-3xl shadow-2xl w-full object-cover"
                  style={{ border: '3px solid #ffde21' }}
                  fetchpriority="high"
                  decoding="async"
                />
              </div>
            </motion.div>

            {/* Text Side */}
            <motion.div
              initial={{ opacity: 0, x: dir === "rtl" ? 50 : -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="md:w-1/2"
            >
              <div className="inline-block mb-4">
                <span
                  className="px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2"
                  style={{
                    background: 'rgba(255, 222, 33, 0.2)',
                    color: '#2d2d2b'
                  }}
                >
                  <Shield size={16} />
                  {dir === "rtl" ? "أمان متطور" : "Advanced Security"}
                </span>
              </div>

              <h1
                className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight"
                style={{ color: '#2d2d2b' }}
              >
                {t("accessControl")}
              </h1>

              <p
                className="text-lg md:text-xl leading-relaxed mb-8"
                style={{ color: '#2d2d2b', opacity: 0.8 }}
              >
                {dir === "rtl"
                  ? "أنظمة أقفال ذكية ومداخل إلكترونية متطورة توفر أقصى درجات الأمان والراحة مع إمكانية التحكم عن بعد"
                  : "Advanced smart lock and electronic access systems providing maximum security and convenience with remote control capabilities"}
              </p>

              <Link to="/design-home">
                <button
                  className="px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                  style={{
                    background: '#ffde21',
                    color: '#2d2d2b'
                  }}
                >
                  {dir === "rtl" ? "احصل على عرض سعر" : "Get a Quote"}
                </button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-4"
              style={{ color: '#2d2d2b' }}
            >
              {dir === "rtl" ? "مميزات النظام" : "System Features"}
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto"
              style={{ color: '#2d2d2b', opacity: 0.7 }}
            >
              {dir === "rtl"
                ? "تقنيات متقدمة للتحكم في المداخل وحماية منزلك أو مكتبك"
                : "Advanced technologies for access control and protecting your home or office"}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative p-8 rounded-2xl transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl group cursor-pointer"
                  style={{
                    background: 'white',
                    border: '2px solid rgba(255, 222, 33, 0.2)'
                  }}
                >
                  {/* Icon Circle */}
                  <div
                    className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6"
                    style={{ background: '#ffde21' }}
                  >
                    <Icon size={32} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
                  </div>

                  {/* Feature Description */}
                  <p
                    className="text-base leading-relaxed"
                    style={{ color: '#2d2d2b' }}
                  >
                    {t(`accessControl_features.${feature.key}`)}
                  </p>

                  {/* Decorative Element */}
                  <div
                    className="absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl transition-all duration-300 group-hover:h-2"
                    style={{ background: '#ffde21' }}
                  ></div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Authentication Methods Section */}
      <section
        className="py-16 md:py-24 relative overflow-hidden"
        style={{ background: '#2d2d2b' }}
      >
        {/* Decorative Background */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(45deg, #ffde21 25%, transparent 25%, transparent 75%, #ffde21 75%, #ffde21),
                             linear-gradient(45deg, #ffde21 25%, transparent 25%, transparent 75%, #ffde21 75%, #ffde21)`,
            backgroundSize: '60px 60px',
            backgroundPosition: '0 0, 30px 30px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-4"
              style={{ color: '#ffde21' }}
            >
              {dir === "rtl" ? "طرق الدخول المتعددة" : "Multiple Access Methods"}
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto"
              style={{ color: 'white', opacity: 0.9 }}
            >
              {dir === "rtl"
                ? "اختر الطريقة الأنسب لك من بين خيارات متعددة"
                : "Choose the most convenient method from multiple options"}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {authMethods.map((method, index) => {
              const Icon = method.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="p-8 rounded-2xl text-center transition-all duration-300 hover:scale-105"
                  style={{ background: 'rgba(255, 222, 33, 0.1)' }}
                >
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ background: '#ffde21' }}
                  >
                    <Icon size={36} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
                  </div>
                  <h3
                    className="text-lg font-bold"
                    style={{ color: 'white' }}
                  >
                    {method.title}
                  </h3>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-4"
              style={{ color: '#2d2d2b' }}
            >
              {dir === "rtl" ? "لماذا الأقفال الذكية؟" : "Why Smart Locks?"}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl"
              style={{
                background: 'white',
                border: '2px solid rgba(255, 222, 33, 0.3)'
              }}
            >
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center mb-6"
                style={{
                  background: 'rgba(255, 222, 33, 0.2)',
                  border: '2px solid #ffde21'
                }}
              >
                <Shield size={32} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
              </div>
              <h3
                className="text-xl font-bold mb-3"
                style={{ color: '#2d2d2b' }}
              >
                {dir === "rtl" ? "أمان محسّن" : "Enhanced Security"}
              </h3>
              <p style={{ color: '#2d2d2b', opacity: 0.8 }}>
                {dir === "rtl"
                  ? "لا حاجة للمفاتيح التقليدية، حماية من السرقة والنسخ"
                  : "No need for traditional keys, protection from theft and duplication"}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl"
              style={{
                background: 'white',
                border: '2px solid rgba(255, 222, 33, 0.3)'
              }}
            >
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center mb-6"
                style={{
                  background: 'rgba(255, 222, 33, 0.2)',
                  border: '2px solid #ffde21'
                }}
              >
                <Smartphone size={32} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
              </div>
              <h3
                className="text-xl font-bold mb-3"
                style={{ color: '#2d2d2b' }}
              >
                {dir === "rtl" ? "تحكم عن بعد" : "Remote Control"}
              </h3>
              <p style={{ color: '#2d2d2b', opacity: 0.8 }}>
                {dir === "rtl"
                  ? "افتح وأغلق الأبواب من أي مكان عبر تطبيق الهاتف"
                  : "Open and close doors from anywhere via mobile app"}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl"
              style={{
                background: 'white',
                border: '2px solid rgba(255, 222, 33, 0.3)'
              }}
            >
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center mb-6"
                style={{
                  background: 'rgba(255, 222, 33, 0.2)',
                  border: '2px solid #ffde21'
                }}
              >
                <DoorOpen size={32} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
              </div>
              <h3
                className="text-xl font-bold mb-3"
                style={{ color: '#2d2d2b' }}
              >
                {dir === "rtl" ? "توافق شامل" : "Universal Compatibility"}
              </h3>
              <p style={{ color: '#2d2d2b', opacity: 0.8 }}>
                {dir === "rtl"
                  ? "متوافق مع جميع أنواع الأبواب والمداخل"
                  : "Compatible with all types of doors and entrances"}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Lock size={60} style={{ color: '#ffde21', margin: '0 auto', marginBottom: '1.5rem' }} />
            <h2
              className="text-3xl md:text-4xl font-black mb-6"
              style={{ color: '#2d2d2b' }}
            >
              {dir === "rtl" ? "جاهز للترقية؟" : "Ready to Upgrade?"}
            </h2>
            <p
              className="text-lg md:text-xl mb-8"
              style={{ color: '#2d2d2b', opacity: 0.7 }}
            >
              {dir === "rtl"
                ? "احصل على نظام أقفال ذكي متطور يجمع بين الأمان والراحة والتحكم الكامل"
                : "Get an advanced smart lock system that combines security, convenience, and full control"}
            </p>
            <Link to="/contact">
              <button
                className="px-10 py-5 rounded-xl font-bold text-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                style={{
                  background: '#ffde21',
                  color: '#2d2d2b'
                }}
              >
                {t("homepage.buttons.contactUs")}
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}